<template>
  <div class="idle-leads-report">
    <div class="bg-white">
      <h1 class="text-2xl font-bold text-gray-900 mb-2">Boşta Kalan Data Raporu</h1>
      <p class="text-sm text-gray-500 mb-6">
        Kaynak bazında, dataların en son ne zaman dokunulduğuna (not / güncelleme / oluşturulma)
        göre boşta geçen gün süreleri. Müşteriye dönüşmüş kayıtlar hariçtir.
      </p>

      <!-- Filters -->
      <div class="filters-container mb-6">
        <div class="flex gap-4 items-end">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Kritik Eşik (Gün)
            </label>
            <input
              v-model.number="threshold"
              type="number"
              min="1"
              class="w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <button
              @click="loadReport"
              :disabled="loading"
              class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="loading">Yükleniyor...</span>
              <span v-else>Raporu Getir</span>
            </button>
          </div>
          <div class="flex-1"></div>
          <div v-if="reportData" class="text-xs text-gray-400 self-center">
            Oluşturulma: {{ reportData.generated_at }}
          </div>
        </div>
      </div>

      <!-- Report table -->
      <div v-if="reportData && !loading" class="report-content">
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer select-none"
                  @click="setSort('source')"
                >
                  Veri Kaynağı (Source) {{ sortIndicator('source') }}
                </th>
                <th
                  class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer select-none"
                  @click="setSort('total_leads')"
                >
                  Toplam Lead Adedi {{ sortIndicator('total_leads') }}
                </th>
                <th
                  class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer select-none"
                  @click="setSort('avg_idle_days')"
                >
                  Ortalama Boşta Kalma Süresi (Gün) {{ sortIndicator('avg_idle_days') }}
                </th>
                <th
                  class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer select-none"
                  @click="setSort('critical_count')"
                >
                  Kritik Durumda (&gt;{{ reportData.threshold }} Gün) {{ sortIndicator('critical_count') }}
                </th>
                <th
                  class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer select-none"
                  @click="setSort('untouched_count')"
                >
                  Hiç Dokunulmamış {{ sortIndicator('untouched_count') }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="row in sortedRows"
                :key="row.source"
                class="hover:bg-gray-50"
              >
                <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                  {{ row.source }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                  {{ formatNumber(row.total_leads) }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-right">
                  <span :class="idleClass(row.avg_idle_days)">
                    {{ formatDays(row.avg_idle_days) }} Gün
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-right">
                  <span
                    class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="row.critical_count > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'"
                  >
                    {{ formatNumber(row.critical_count) }} Adet
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                  {{ formatNumber(row.untouched_count) }}
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-50">
              <tr>
                <td class="px-4 py-3 text-sm font-bold text-gray-900">TOPLAM</td>
                <td class="px-4 py-3 text-sm text-right font-bold text-gray-900">
                  {{ formatNumber(reportData.totals.total_leads) }}
                </td>
                <td class="px-4 py-3 text-sm text-right font-bold text-gray-900">
                  {{ formatDays(reportData.totals.avg_idle_days) }} Gün
                </td>
                <td class="px-4 py-3 text-sm text-right font-bold text-gray-900">
                  {{ formatNumber(reportData.totals.critical_count) }} Adet
                </td>
                <td class="px-4 py-3 text-sm text-right font-bold text-gray-900">
                  {{ formatNumber(reportData.totals.untouched_count) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-2 text-gray-600">Rapor yükleniyor...</span>
      </div>

      <!-- Empty state -->
      <div
        v-if="reportData && !loading && reportData.data.length === 0"
        class="text-center py-12"
      >
        <div class="text-gray-500 text-lg mb-2">Veri bulunamadı</div>
        <div class="text-gray-400">Raporlanacak lead kaydı bulunmamaktadır.</div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '../services/http'

export default {
  name: 'IdleLeadsReport',
  data() {
    return {
      threshold: 30,
      reportData: null,
      loading: false,
      sortKey: 'avg_idle_days',
      sortDir: 'desc'
    }
  },
  mounted() {
    // Yetki UX kontrolü: cache.me async yüklendiği için sadece department
    // BİLİNİYORSA ve yetkisizse redirect et. Asıl güvenlik backend guard'da.
    const me = (this.$root.cache || {}).me || {}
    if (
      me.department &&
      !['admin', 'admin1', 'admin2', 'sales_manager'].includes(me.department)
    ) {
      this.$router.push('/')
      return
    }
    this.loadReport()
  },
  computed: {
    sortedRows() {
      if (!this.reportData) return []
      const rows = [...this.reportData.data]
      const key = this.sortKey
      const dir = this.sortDir === 'asc' ? 1 : -1
      rows.sort((a, b) => {
        if (key === 'source') {
          return a.source.localeCompare(b.source, 'tr') * dir
        }
        return (a[key] - b[key]) * dir
      })
      return rows
    }
  },
  methods: {
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortDir = key === 'source' ? 'asc' : 'desc'
      }
    },

    sortIndicator(key) {
      if (this.sortKey !== key) return ''
      return this.sortDir === 'desc' ? '▼' : '▲'
    },

    async loadReport() {
      this.loading = true
      try {
        const response = await http.getIdleLeadsReport({
          threshold: this.threshold || 30
        })

        if (response.data.success) {
          this.reportData = response.data
        } else {
          alert(response.data.message || 'Bir hata oluştu')
        }
      } catch (error) {
        console.error('Report loading error:', error)
        alert('Rapor yüklenirken bir hata oluştu')
      } finally {
        this.loading = false
      }
    },

    formatNumber(value) {
      return (Number(value) || 0).toLocaleString('tr-TR')
    },

    formatDays(value) {
      return (Number(value) || 0).toLocaleString('tr-TR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })
    },

    idleClass(days) {
      const threshold = this.reportData ? this.reportData.threshold : 30
      if (days > threshold) return 'text-red-600 font-semibold'
      if (days > threshold / 2) return 'text-yellow-600 font-medium'
      return 'text-green-600'
    }
  }
}
</script>

<style scoped>
.idle-leads-report {
  @apply max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8;
}

.filters-container {
  @apply bg-gray-50 rounded-lg p-4;
}

.report-content {
  @apply mt-6;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
